/**
 * Maps stored values from retired vocabularies onto the options the editor
 * offers now, so a select never holds a value it cannot show.
 *
 * @module utils/legacy-vocabulary
 */

import type { Programme, Stage } from "@/types";

import { stageTeachingPeriodOptions } from "./teaching-period";

/** Retired award type labels, by the option that replaced each. */
export const LEGACY_AWARD_TYPES: Readonly<Record<string, string>> = {
  "Master's Degree": "Masters",
  "Doctoral Degree": "Doctoral",
  "FET Minor Award": "FE Minor Award",
  "Special Purpose Award": "FE Special Purpose Award",
  "Advanced Certificate": "FE Major Award",
};

/** Retired award standard ids, by the catalogue id that replaced each. */
export const LEGACY_AWARD_STANDARD_IDS: Readonly<Record<string, string>> = {
  "generic-post-gradiuate-diploma": "generic-postgraduate-diploma",
};

/** A standard id, or an indicator id it prefixes, under its current catalogue id. */
function currentStandardId(id: string): string;
function currentStandardId(id: string | undefined): string | undefined;
function currentStandardId(id: string | undefined): string | undefined {
  const [standard = "", ...rest] = id?.split(":") ?? [];
  const current = LEGACY_AWARD_STANDARD_IDS[standard];
  return current ? [current, ...rest].join(":") : id;
}

function normaliseStandardIds<T extends Programme>(programme: T): Partial<T> {
  const ids = programme.awardStandardIds?.map((id) => currentStandardId(id));
  const awards = programme.embeddedAwards?.map((award) => {
    const awardStandardId = currentStandardId(award.awardStandardId);
    return awardStandardId === award.awardStandardId ? award : { ...award, awardStandardId };
  });
  const plos = programme.plos?.map((plo) => {
    const mappings = plo.standardMappings?.map((mapping) => {
      const standardId = currentStandardId(mapping.standardId);
      const indicatorId = currentStandardId(mapping.indicatorId);
      return standardId === mapping.standardId && indicatorId === mapping.indicatorId
        ? mapping
        : { ...mapping, standardId, indicatorId };
    });
    return mappings?.some((mapping, index) => mapping !== plo.standardMappings?.[index])
      ? { ...plo, standardMappings: mappings }
      : plo;
  });
  const changed: Partial<T> = {};
  if (ids?.some((id, index) => id !== programme.awardStandardIds?.[index])) {
    changed.awardStandardIds = ids;
  }
  if (awards?.some((award, index) => award !== programme.embeddedAwards?.[index])) {
    changed.embeddedAwards = awards;
  }
  if (plos?.some((plo, index) => plo !== programme.plos?.[index])) {
    changed.plos = plos;
  }
  return changed;
}

const NUMBERED_PERIOD = /^(?:semester|teaching period|tp|s)\s*(\d+)$/i;

/**
 * "Semester 2" or "TP2" becomes the option value "2", numbered within the
 * stage. "Both" is the whole stage's option; anything else is unchanged.
 */
export function canonicalTeachingPeriod(
  value: string | undefined,
  stageCount = 0,
): string | undefined {
  if (stageCount > 1 && value?.trim().toLowerCase() === "both") {
    return stageTeachingPeriodOptions(stageCount).at(-1)?.value;
  }
  const match = value?.trim().match(NUMBERED_PERIOD);
  return match ? String(Number(match[1])) : value;
}

function normaliseStage(stage: Stage): Stage {
  const count = Number(stage.semesterCount ?? 0);
  const modules = stage.modules?.map((entry) => {
    const semester = canonicalTeachingPeriod(entry.semester, count);
    return semester === entry.semester ? entry : { ...entry, semester };
  });
  const slots = stage.electiveChoiceSlots?.map((slot) => {
    const semester = canonicalTeachingPeriod(slot.semester, count);
    return semester === slot.semester ? slot : { ...slot, semester };
  });
  const changed =
    modules?.some((entry, index) => entry !== stage.modules?.[index]) === true ||
    slots?.some((slot, index) => slot !== stage.electiveChoiceSlots?.[index]) === true;
  return changed ? { ...stage, modules, electiveChoiceSlots: slots } : stage;
}

/** The programme with legacy values mapped; the same object when none apply. */
export function normaliseLegacyVocabulary<T extends Programme>(programme: T): T {
  const awardType = LEGACY_AWARD_TYPES[programme.awardType ?? ""] ?? programme.awardType;
  const versions = programme.versions?.map((version) => {
    const stages = version.stages?.map(normaliseStage);
    return stages?.some((stage, index) => stage !== version.stages?.[index])
      ? { ...version, stages }
      : version;
  });
  const versionsChanged = versions?.some(
    (version, index) => version !== programme.versions?.[index],
  );
  const standards = normaliseStandardIds(programme);
  if (awardType === programme.awardType && !versionsChanged && !Object.keys(standards).length) {
    return programme;
  }
  return { ...programme, awardType, versions, ...standards };
}
